import React, { useState } from "react";
import { Plus, Trash2, User, Users, Shield, CheckCircle, XCircle } from "lucide-react";
import Mo3tiButton from "../../layout/Mo3tiButton";
import { apiFetch } from "../../../../../context/AuthContext";
import ReusableDropdown from "../../../../../components/ReusableDropdown";

const FamilyMembersStep = ({
  selectedEmp,
  members,
  tiers,
  loading,
  fetchMembers,
  fetchEmployees,
  API_BASE_URL,
  currentTheme,
  t,
  isDark,
  isArabic,
  getAuthHeaders,
}) => {
  const [addingMember, setAddingMember] = useState(false);
  const [savingMember, setSavingMember] = useState(false);
  const [newMember, setNewMember] = useState({ name: "", relationship: "", tierId: "" });
  const [error, setError] = useState("");

  const totalMonthly = members.reduce((s, m) => s + (m.isActive ? m.tier?.monthlyPrice || 0 : 0), 0);

  const styles = {
    container: { display: "flex", flexDirection: "column", gap: "24px" },
    summaryCards: { display: "flex", gap: "16px", flexWrap: "wrap" },
    summaryCard: {
      flex: 1,
      minWidth: "200px",
      padding: "20px",
      borderRadius: "20px",
      background: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.01)",
      border: `1px solid ${currentTheme.colors.border}`,
      display: "flex",
      alignItems: "center",
      gap: "16px",
    },
    summaryIcon: {
      width: "48px",
      height: "48px",
      borderRadius: "14px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    tableContainer: {
      borderRadius: "20px",
      overflow: "hidden",
      border: `1px solid ${currentTheme.colors.border}`,
      background: isDark ? "rgba(255,255,255,0.02)" : "#fff",
    },
    table: { width: "100%", borderCollapse: "collapse" },
    th: {
      padding: "16px 20px",
      textAlign: isArabic ? "right" : "left",
      background: isDark ? "rgba(255,255,255,0.05)" : "#f8fafc",
      color: currentTheme.colors.textSecondary,
      fontSize: "13px",
      fontWeight: "700",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    },
    td: {
      padding: "16px 20px",
      borderTop: `1px solid ${currentTheme.colors.border}`,
      color: currentTheme.colors.textPrimary,
      fontSize: "15px",
    },
    form: {
      padding: "24px",
      borderRadius: "20px",
      background: isDark ? "rgba(255,255,255,0.05)" : "#f8fafc",
      border: `1px solid ${currentTheme.colors.border}`,
      display: "flex",
      flexDirection: "column",
      gap: "20px",
    },
    input: {
      width: "100%",
      padding: "12px 16px",
      borderRadius: "12px",
      border: `1px solid ${currentTheme.colors.border}`,
      background: isDark ? "rgba(15, 23, 42, 0.8)" : "#fff",
      color: currentTheme.colors.textPrimary,
      fontSize: "14px",
      outline: "none",
    },
    label: {
      fontSize: "13px",
      fontWeight: "600",
      color: currentTheme.colors.textSecondary,
      marginBottom: "8px",
      display: "block",
    },
  };

  const handleAddMember = async () => {
    if (!newMember.name.trim() || !newMember.relationship.trim() || !newMember.tierId) {
      setError(t("miFillAllFields"));
      return;
    }
    setSavingMember(true);
    setError("");
    try {
      const r = await apiFetch(`${API_BASE_URL}/payrollX/medical-insurance/employees/${encodeURIComponent(selectedEmp.infinityCode)}/members`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          name: newMember.name.trim(),
          relationship: newMember.relationship.trim(),
          tierId: parseInt(newMember.tierId),
        }),
      });
      if (!r.ok) throw new Error();
      await fetchMembers();
      await fetchEmployees();
      setNewMember({ name: "", relationship: "", tierId: "" });
      setAddingMember(false);
    } catch (e) {
      setError(t("miNetworkError"));
    } finally {
      setSavingMember(false);
    }
  };

  const toggleActive = async (id, current) => {
    try {
      await apiFetch(`${API_BASE_URL}/payrollX/medical-insurance/members/${id}`, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify({ isActive: !current }),
      });
      await fetchMembers();
      await fetchEmployees();
    } catch (e) {}
  };

  const removeMember = async (id) => {
    if (!window.confirm(t("confirmDelete"))) return;
    try {
      await apiFetch(`${API_BASE_URL}/payrollX/medical-insurance/members/${id}`, {
        method: "DELETE",
        headers: getAuthHeaders(),
      });
      await fetchMembers();
      await fetchEmployees();
    } catch (e) {}
  };

  if (loading) return <div style={{ textAlign: "center", padding: "40px", color: currentTheme.colors.textSecondary }}>{t("loading")}...</div>;

  return (
    <div style={styles.container}>
      <div style={styles.summaryCards}>
        <div style={styles.summaryCard}>
          <div style={{ ...styles.summaryIcon, background: currentTheme.colors.primary + "15", color: currentTheme.colors.primary }}>
            <Users size={24} />
          </div>
          <div>
            <div style={{ fontSize: "20px", fontWeight: "800", color: currentTheme.colors.textPrimary }}>
              {members.filter(m => m.isActive).length}
            </div>
            <div style={{ fontSize: "13px", color: currentTheme.colors.textSecondary }}>{t("miMembers")}</div>
          </div>
        </div>
        <div style={styles.summaryCard}>
          <div style={{ ...styles.summaryIcon, background: currentTheme.colors.success + "15", color: currentTheme.colors.success }}>
            <Shield size={24} />
          </div>
          <div>
            <div style={{ fontSize: "20px", fontWeight: "800", color: currentTheme.colors.success }}>
              {totalMonthly.toLocaleString()} EGP
            </div>
            <div style={{ fontSize: "13px", color: currentTheme.colors.textSecondary }}>{t("miTotalMonthly")}</div>
          </div>
        </div>
      </div>

      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>{t("fullName")}</th>
              <th style={styles.th}>{t("miRelationship")}</th>
              <th style={styles.th}>{t("miInsuranceTier")}</th>
              <th style={styles.th}>{t("miMonthlyCost")}</th>
              <th style={styles.th}>{t("actions")}</th>
            </tr>
          </thead>
          <tbody>
            {members.length > 0 ? (
              members.map((m) => (
                <tr key={m.id} style={{ opacity: m.isActive ? 1 : 0.6 }}>
                  <td style={styles.td}>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <User size={16} color={currentTheme.colors.textSecondary} />
                      <span style={{ fontWeight: "600" }}>{m.name}</span>
                    </div>
                  </td>
                  <td style={styles.td}>{m.relationship}</td>
                  <td style={styles.td}>{m.tier?.tierName || "—"}</td>
                  <td style={{ ...styles.td, fontWeight: "700", color: m.isActive ? currentTheme.colors.success : currentTheme.colors.textSecondary }}>
                    {m.isActive ? `${m.tier?.monthlyPrice?.toLocaleString()} EGP` : "—"}
                  </td>
                  <td style={styles.td}>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <Mo3tiButton
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleActive(m.id, m.isActive)}
                        icon={m.isActive ? XCircle : CheckCircle}
                        style={{ color: m.isActive ? currentTheme.colors.danger : currentTheme.colors.success }}
                      />
                      <Mo3tiButton
                        variant="ghost"
                        size="sm"
                        onClick={() => removeMember(m.id)}
                        icon={Trash2}
                        style={{ color: currentTheme.colors.danger }}
                      />
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5} style={{ ...styles.td, textAlign: "center", padding: "40px", color: currentTheme.colors.textSecondary }}>
                  {t("miNoFamilyMembers")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {!addingMember ? (
        <Mo3tiButton variant="primary" onClick={() => setAddingMember(true)} icon={Plus}>
          {t("miAddFamilyMember")}
        </Mo3tiButton>
      ) : (
        <div style={styles.form}>
          <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "700", color: currentTheme.colors.textPrimary }}>{t("miNewFamilyMember")}</h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(180px, 100%), 1fr))", gap: "16px" }}>
            <div>
              <label style={styles.label}>{t("fullName")}</label>
              <input
                style={styles.input}
                placeholder={t("miFullNamePlaceholder")}
                value={newMember.name}
                onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
              />
            </div>
            <div>
              <label style={styles.label}>{t("miRelationship")}</label>
              <input
                style={styles.input}
                placeholder={t("miRelationshipPlaceholder")}
                value={newMember.relationship}
                onChange={(e) => setNewMember({ ...newMember, relationship: e.target.value })}
              />
            </div>
            <div>
              <label style={styles.label}>{t("miInsuranceTier")}</label>
              <ReusableDropdown
                items={tiers}
                value={newMember.tierId}
                onChange={(value) => setNewMember({ ...newMember, tierId: String(value) })}
                placeholder={t("miSelectTier")}
                getItemKey={(tier) => tier.id}
                getItemLabel={(tier) => `${tier.tierName} (${tier.monthlyPrice.toLocaleString()} EGP)`}
                searchable={tiers.length > 7}
                theme={isDark ? "dark" : "light"}
                language={isArabic ? "ar" : "en"}
              />
            </div>
          </div>
          {error && <p style={{ color: currentTheme.colors.danger, fontSize: "13px", margin: 0 }}>{error}</p>}
          <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
            <Mo3tiButton variant="secondary" onClick={() => setAddingMember(false)}>
              {t("cancel")}
            </Mo3tiButton>
            <Mo3tiButton variant="primary" onClick={handleAddMember} disabled={savingMember}>
              {savingMember ? t("miSaving") : t("save")}
            </Mo3tiButton>
          </div>
        </div>
      )}
    </div>
  );
};

export default FamilyMembersStep;